import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { HR_NAV, TRAINING_COLORS } from './nav';

const STATUSES = ['assigned', 'in_progress', 'completed', 'expired'];

const Training: React.FC = () => {
    const { api } = useInternalApi();
    const [records, setRecords] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [people, setPeople] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ internal_user_id: '', title: '', provider: '', is_mandatory: false, due_on: '' });

    useEffect(() => { document.title = 'Training & CPD - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const r = await api.get(`hr-api/training/?${params}`);
            if (r.data?.status === 'success') {
                setRecords(r.data.data.records || []);
                setSummary(r.data.data.summary);
            }
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('hr-api/reference/').then(r => { if (r.data?.status === 'success') setPeople(r.data.data.people || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!form.internal_user_id || !form.title.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Choose a person and give the training a title.' });
            return;
        }
        try {
            await api.post('hr-api/training/', form);
            setShowAdd(false);
            setForm({ internal_user_id: '', title: '', provider: '', is_mandatory: false, due_on: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not assign training.' });
        }
    };

    const setStatus = async (t: any, status: string) => {
        try { await api.patch(`hr-api/training/${t.id}/`, { status }); load(); }
        catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update.' }); }
    };

    return (
        <InternalShell activeDashboard="human_resources" subNav={HR_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faGraduationCap} className="me-2 text-primary" />Training & CPD</h2>
                    <p className="text-muted mb-0">Assigned learning and mandatory-training compliance</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Assign Training</button>
            </div>

            {loading || !summary ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Mandatory compliance</div>
                            <h3 className={summary.mandatory_compliance_pct === 100 ? 'text-success' : 'text-warning'}>
                                {summary.mandatory_compliance_pct === null ? '—' : `${summary.mandatory_compliance_pct}%`}
                            </h3>
                            <div className="small text-muted">{summary.mandatory_completed} of {summary.mandatory_total}</div>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Total assigned</div><h4>{summary.total}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Completed</div><h4 className="text-success">{summary.completed}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Overdue</div>
                            <h4 className={summary.overdue ? 'text-danger' : ''}>{summary.overdue}</h4>
                        </div></div></div>
                    </div>

                    <select className="form-select mb-3" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">All statuses</option>
                        {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {records.length === 0 ? (
                                <div className="text-center py-5 text-muted">No training records.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Person</th><th>Training</th><th>Mandatory</th><th>Due</th><th>Status</th><th>Move to</th></tr></thead>
                                    <tbody>
                                        {records.map(t => (
                                            <tr key={t.id} className={t.is_overdue ? 'table-danger' : ''}>
                                                <td>{t.person}</td>
                                                <td><strong>{t.title}</strong>{t.provider && <div className="small text-muted">{t.provider}</div>}</td>
                                                <td>{t.is_mandatory ? <span className="badge bg-warning">Mandatory</span> : '—'}</td>
                                                <td className="small">{t.due_on || '—'}{t.is_overdue && <span className="badge bg-danger ms-1">overdue</span>}</td>
                                                <td><span className={`badge bg-${TRAINING_COLORS[t.status]}`}>{t.status.replace('_', ' ')}</span></td>
                                                <td style={{ minWidth: 150 }}>
                                                    <select className="form-select form-select-sm" value={t.status} onChange={(e) => setStatus(t, e.target.value)}>
                                                        {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Assign Training</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Person</label>
                                    <select className="form-select" value={form.internal_user_id} onChange={(e) => setForm(f => ({ ...f, internal_user_id: e.target.value }))}>
                                        <option value="">Select…</option>
                                        {people.map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.department})</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Training title</label>
                                    <input className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Provider</label>
                                        <input className="form-control" value={form.provider} onChange={(e) => setForm(f => ({ ...f, provider: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Due on</label>
                                        <input type="date" className="form-control" value={form.due_on} onChange={(e) => setForm(f => ({ ...f, due_on: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="mand" checked={form.is_mandatory}
                                        onChange={(e) => setForm(f => ({ ...f, is_mandatory: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="mand">Mandatory — counts towards compliance</label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Assign</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Training;
